//src/components/Spotify/spotifyMusicPlayer.js
// Updated SpotifyMusicPlayer.js - With Radio Mode integration using useRadioMode hook
import React, { useState, useEffect, useRef } from 'react';
import { usePlayerContext } from '../../hooks/PlayerContext';
import { useSyncOperation, SyncOperations } from '../../hooks/SyncCoordinator';
import SpotifySearchModal from './SpotifySearchModal';
import { logUserAction } from '../../utils/basicBehaviorLogger';
import logger from '../../services/logger';

const SpotifyMusicPlayer = ({ disableOldRadio = false }) => {
  const {
    state,
    handlePlayPause,
    handleNextTrack,
    handlePrevTrack,
    handleSeek,
    setCurrentTrackIndex,
    reorderPlaylistTracks,
    removeTrackFromPlaylist,
    toggleShuffle,
    toggleRepeat,
    radioMode,
    toggleRadioMode,
  } = usePlayerContext();

  const { runSync } = useSyncOperation();

  // Use the shared radioMode from PlayerContext (already integrated)
  const radioHook = radioMode;
  
  // Local player states
  const [rotationAngle, setRotationAngle] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const [isTrackProtected, setIsTrackProtected] = useState(false);
  
  // Refs for UI handling
  const progressBarRef = useRef(null);
  const animationRef = useRef(null);
  const isDraggingRef = useRef(false);
  
  // Enhanced refs for track end handling
  const trackEndTimeoutRef = useRef(null);
  const lastSeekTimeRef = useRef(0);
  const MIN_SEEK_INTERVAL = 1000;
  
  // Extract current state values
  const {
    playlist,
    currentTrackIndex,
    isPlaying,
    progressMs,
    durationMs
  } = state;
  
  const tracks = playlist.tracks;
  const currentTrack = tracks[currentTrackIndex] || {};
  
  // Monitor AI track protection status
  useEffect(() => {
    const checkTrackProtection = () => {
      if (window._aiTrackProtectionStatus) {
        const status = window._aiTrackProtectionStatus();
        setIsTrackProtected(status && status.active);
      } else {
        setIsTrackProtected(false);
      }
    };
    
    // Check every 100ms for responsive UI updates
    const interval = setInterval(checkTrackProtection, 100);
    checkTrackProtection(); // Initial check
    
    return () => clearInterval(interval);
  }, []);

  // Force re-render when track changes
  const [trackUpdateKey, setTrackUpdateKey] = useState(0);
  useEffect(() => {
    setTrackUpdateKey(prev => prev + 1);
  }, [currentTrackIndex, currentTrack?.uri, currentTrack?.name, tracks.length]);
  
  // Rotation Animation
  useEffect(() => {
    const rotate = () => {
      setRotationAngle((prev) => prev + 0.15);
      animationRef.current = requestAnimationFrame(rotate);
    };
    
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(rotate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);
  
  // Standard track end handling for repeat mode
  useEffect(() => {
    if (!isPlaying || !durationMs || state.repeatMode !== 'track') {
      if (trackEndTimeoutRef.current) {
        clearTimeout(trackEndTimeoutRef.current);
        trackEndTimeoutRef.current = null;
      }
      return;
    }
    
    const timeRemaining = durationMs - progressMs;
    
    if (timeRemaining > 0 && timeRemaining < 3000) {
      if (trackEndTimeoutRef.current) {
        return;
      }
      
      trackEndTimeoutRef.current = setTimeout(() => {
        trackEndTimeoutRef.current = null;
        
        const now = Date.now();
        if (now - lastSeekTimeRef.current < MIN_SEEK_INTERVAL) {
          return;
        }
        
        runSync(SyncOperations.PLAYBACK_CONTROL, async () => {
          try {
            lastSeekTimeRef.current = now;
            await handleSeek(0);
          } catch (error) {
            logger.error("Error during track repeat:", error);
          }
        });
      }, Math.max(50, timeRemaining - 500));
      
      return () => {
        if (trackEndTimeoutRef.current) {
          clearTimeout(trackEndTimeoutRef.current);
          trackEndTimeoutRef.current = null;
        }
      };
    }
    
    return undefined;
  }, [isPlaying, progressMs, durationMs, state.repeatMode, handleSeek, runSync]);

  // Track completion detection and auto-advance
  useEffect(() => {
    if (!isPlaying || !durationMs || !progressMs) {
      return;
    }

    const timeRemaining = durationMs - progressMs;

    if (timeRemaining > 0 && timeRemaining <= 2000) {
      const currentTrack = tracks[currentTrackIndex];

      if (currentTrack && !window._playerStateFlags.trackEndProcessed) {
        window._playerStateFlags.trackEndProcessed = true;

        // Log track completion
        logUserAction({
          action: 'complete',
          trackUri: currentTrack.uri,
          trackName: currentTrack.name,
          artist: currentTrack.artists?.[0]?.name || 'Unknown Artist',
          position: durationMs,
          timestamp: Date.now()
        });

        // Auto-advance to next track (unless repeat track mode)
        if (state.repeatMode !== 'track') {
          setTimeout(() => {
            // Mark as natural progression (allow during DJ speech)
            if (typeof window !== 'undefined') {
              window._naturalProgression = true;
            }

            runSync(SyncOperations.TRACK_CHANGE, async () => {
              await handleNextTrack();

              // Clear natural progression flag
              if (typeof window !== 'undefined') {
                window._naturalProgression = false;
              }
            });
          }, 1500);
        }
      }
    } else {
      // Reset the flag when we're not near the end
      if (window._playerStateFlags.trackEndProcessed && timeRemaining > 3000) {
        window._playerStateFlags.trackEndProcessed = false;
      }
    }
  }, [isPlaying, progressMs, durationMs, currentTrackIndex, tracks, state.repeatMode, handleNextTrack, runSync]);
  
  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (trackEndTimeoutRef.current) {
        clearTimeout(trackEndTimeoutRef.current);
        trackEndTimeoutRef.current = null;
      }
    };
  }, []);
  
  // Format time helper
  const formatTime = (ms) => {
    if (!ms) return '0:00';
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };
  
  // Toggle radio mode using the context method
  const handleToggleRadio = () => {
    toggleRadioMode();
  };
  
  // Repeat toggle — delegates to context; keeps seek-to-0 behavior when activating track repeat
  const handleToggleRepeat = () => {
    runSync(SyncOperations.PLAYBACK_CONTROL, async () => {
      const modes = ['off', 'context', 'track'];
      const nextMode = modes[(modes.indexOf(state.repeatMode) + 1) % modes.length];
      toggleRepeat();
      if (nextMode === 'track' && progressMs > 0 && durationMs > 0) {
        const remainingPercent = (durationMs - progressMs) / durationMs;
        if (remainingPercent < 0.1) {
          lastSeekTimeRef.current = Date.now();
          await handleSeek(0);
        }
      }
    });
  };

  // Shuffle — delegates to context; generates index-permutation order, never mutates tracks array
  const handleShufflePlaylist = () => {
    runSync(SyncOperations.PLAYBACK_CONTROL, async () => {
      toggleShuffle();
    });
  };
  
  // Progress bar click handler
  const handleProgressBarClick = async (e) => {
    if (!durationMs || isDraggingRef.current) return;
    
    e.preventDefault();
    
    await runSync(SyncOperations.PLAYBACK_CONTROL, async () => {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.max(0, Math.min(1, clickX / width));
      const newPosition = Math.floor(percentage * durationMs);
      
      lastSeekTimeRef.current = Date.now();
      await handleSeek(newPosition);
    });
  };
  
  // Synchronized controls
  const handlePlayPauseSync = () => {
    runSync(SyncOperations.PLAYBACK_CONTROL, async () => {
      await handlePlayPause();
    });
  };
  
  const handleNextSync = () => {
    // Check skip blocking from new Radio Mode system
    if (radioHook.isActive) {
      const blockInfo = radioHook.shouldBlockUserAction('skip');
      if (blockInfo.blocked && !window._naturalProgression) {
        logger.debug('Skipping blocked:', blockInfo.reason);
        return;
      }
    }

    runSync(SyncOperations.TRACK_CHANGE, async () => {
      await handleNextTrack();
    });
  };
  
  const handlePrevSync = () => {
    // Check skip blocking from new Radio Mode system
    if (radioHook.isActive) {
      const blockInfo = radioHook.shouldBlockUserAction('previous');
      if (blockInfo.blocked && !window._naturalProgression) {
        logger.debug('Previous blocked:', blockInfo.reason);
        return;
      }
    }

    runSync(SyncOperations.TRACK_CHANGE, async () => {
      if (currentTrackIndex <= 0 && state.repeatMode === 'context') {
        setCurrentTrackIndex(tracks.length - 1);
      } else {
        await handlePrevTrack();
      }
    });
  };
  
  const handleSelectTrack = (index) => {
    if (index === currentTrackIndex) return;

    // Check skip blocking from new Radio Mode system
    if (radioHook.isActive) {
      const blockInfo = radioHook.shouldBlockUserAction('track_select');
      if (blockInfo.blocked && !window._naturalProgression) {
        logger.debug('Track selection blocked:', blockInfo.reason);
        return;
      }
    }

    runSync(SyncOperations.TRACK_CHANGE, async () => {
      setCurrentTrackIndex(index);
      await new Promise(resolve => setTimeout(resolve, 50));
    });
  };
  
  const handleRemoveTrack = (index, e) => {
    e.stopPropagation();
    
    runSync(SyncOperations.PLAYLIST_UPDATE, async () => {
      removeTrackFromPlaylist(index);
    });
  };
  
  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    isDraggingRef.current = true;
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragEnd = () => {
    isDraggingRef.current = false;
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (!isNaN(draggedIndex) && draggedIndex !== dropIndex) {
      runSync(SyncOperations.PLAYLIST_UPDATE, async () => {
        reorderPlaylistTracks(draggedIndex, dropIndex);
      });
    }

    isDraggingRef.current = false;
  };

  // Get current track info
  const getTrackInfo = () => {
    const track = tracks[currentTrackIndex];
    if (!track) {
      return {
        image: '/default-album.png',
        title: 'No Track Playing',
        artist: 'Select a track to start',
        hasTrack: false
      };
    }
    
    return {
      image: track.images?.[0]?.url || '/default-album.png',
      title: track.name || 'Unknown Track',
      artist: track.artists || 'Unknown Artist',
      hasTrack: true
    };
  };
  
  const trackInfo = getTrackInfo();
  
  return (
    <>
      {/* Main sidebar */}
      <div className={`music-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Header */}
        <div className="sidebar-header" key={`header-${trackUpdateKey}`}>
          <div className="sidebar-header-top">
            <h3>Now Playing</h3>
            <button 
              className="collapse-button" 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label="Collapse sidebar"
            >
              ⟨
            </button>
          </div>
          
          <div className="current-track-display">
            {trackInfo.hasTrack ? (
              <>
                <span className="current-track-name">{trackInfo.title}</span>
                {trackInfo.artist && (
                  <span> by {trackInfo.artist}</span>
                )}
              </>
            ) : (
              <span>No track selected</span>
            )}
          </div>
        </div>
        
        {/* Current track view */}
        <div className="now-playing-section" key={`track-${trackUpdateKey}`}>
          <div className="album-artwork">
            <img
              src={trackInfo.image}
              alt={trackInfo.title}
              className={isPlaying ? 'rotating' : ''}
              style={{ transform: `rotate(${rotationAngle}deg)` }}
              onError={(e) => {
                e.target.src = '/default-album.png';
              }}
            />
          </div>
          
          <div className="track-info">
            <h2 className="track-title">{trackInfo.title}</h2>
            <p className="track-artist">{trackInfo.artist}</p>
          </div>
          
          {/* Progress bar */}
          <div 
            className="progress-container"
            ref={progressBarRef}
            onClick={handleProgressBarClick}
          >
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${durationMs ? (progressMs / durationMs) * 100 : 0}%` }}
              />
            </div>
            <div className="time-display">
              <span>{formatTime(progressMs)}</span>
              <span>{formatTime(durationMs)}</span>
            </div>
          </div>
          
          {/* Playback controls */}
          <div className="player-controls">
            <button 
              className="control-button"
              onClick={handlePrevSync}
              disabled={isTrackProtected || (currentTrackIndex <= 0 && state.repeatMode !== 'context')}
              title={isTrackProtected ? 'AI preparing - controls disabled' : ''}
            >
              ⏮
            </button>
            <button 
              className="play-button"
              onClick={handlePlayPauseSync}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button 
              className="control-button"
              onClick={handleNextSync}
              disabled={isTrackProtected || (currentTrackIndex >= tracks.length - 1 && state.repeatMode !== 'context' && !radioHook.isActive)}
              title={isTrackProtected ? 'AI preparing - controls disabled' : ''}
            >
              ⏭
            </button>
          </div>
          
          {/* Extra controls with Radio Mode button */}
          <div className="extra-controls">
            <button
              className={`shuffle-button ${state.shuffleMode ? 'active' : ''}`}
              onClick={handleShufflePlaylist}
              title={state.shuffleMode ? "Disable shuffle" : "Shuffle playlist"}
            >
              🔀
            </button>
            
            {/* Radio Mode Button using hook state */}
            <button
              className={`radio-button ${radioHook.isActive ? 'active' : ''}`}
              onClick={handleToggleRadio}
              title={radioHook.isActive ? "Disable radio mode" : "Enable radio mode - continuous similar music"}
              style={{
                background: radioHook.isActive ? 'rgba(29, 185, 84, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                border: `1px solid ${radioHook.isActive ? 'var(--player-accent)' : 'rgba(255, 255, 255, 0.2)'}`,
                color: radioHook.isActive ? 'var(--player-accent)' : 'var(--player-secondary)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                padding: '6px 12px',
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Radio
            </button>

            {/* DJ Speech Toggle - only visible when Radio Mode is active */}
            {radioHook.isActive && (
              <button
                className={`dj-speech-button ${radioHook.djSpeechEnabled ? 'active' : ''}`}
                onClick={radioHook.toggleDJSpeech}
                title={radioHook.djSpeechEnabled ? "Disable DJ speech" : "Enable DJ speech - AI introductions"}
                style={{
                  background: radioHook.djSpeechEnabled ? 'rgba(138, 43, 226, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${radioHook.djSpeechEnabled ? '#8A2BE2' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: radioHook.djSpeechEnabled ? '#8A2BE2' : 'var(--player-secondary)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  transition: 'all 0.2s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                🎙️ DJ
              </button>
            )}

            <button
              className={`repeat-button ${state.repeatMode !== 'off' ? 'active' : ''}`}
              onClick={handleToggleRepeat}
              title={
                state.repeatMode === 'off' ? "Enable repeat" :
                state.repeatMode === 'context' ? "Repeat all — click for repeat track" :
                "Repeat track — click to disable"
              }
            >
              {state.repeatMode === 'track' ? '🔂' : '🔁'}
            </button>
          </div>
        </div>

        {/* Playlist section */}
        <div className="playlist-section">
          <div className="playlist-header">
            <h4>
              {playlist.name || 'Queue'} ({tracks.length})
              {radioHook.isActive && (
                <span style={{ 
                  fontSize: '11px', 
                  color: 'var(--player-accent)', 
                  marginLeft: '8px',
                  padding: '2px 6px',
                  backgroundColor: 'rgba(29, 185, 84, 0.2)',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Radio
                </span>
              )}
            </h4>
            <button
              className="search-button"
              onClick={() => setShowSearch(true)}
              title="Add tracks to queue"
            >
              🔍
            </button>
          </div>

          {/* Playlist tracks with DJ placards */}
          <div className="playlist-tracks">
            {tracks.length > 0 ? (
              tracks.map((item, index) => {
                const track = item;
                return (
                  <div
                    key={`${track.uri}-${index}-${trackUpdateKey}`}
                    className={`playlist-track ${index === currentTrackIndex ? 'active' : ''} ${isTrackProtected ? 'protected' : ''}`}
                    onClick={() => handleSelectTrack(index)}
                    title={isTrackProtected ? 'AI preparing next track - selection disabled' : ''}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <span className="track-number">{index + 1}</span>
                    <div className="track-thumb">
                      <img
                        src={track.images?.[0]?.url || '/default-album.png'}
                        alt=""
                        onError={(e) => {
                          e.target.src = '/default-album.png';
                        }}
                      />
                      {index === currentTrackIndex && <div className="playing-indicator">▶</div>}
                    </div>
                    <div className="track-details">
                      <div className="track-name">{track.name || 'Unknown Track'}</div>
                      <div className="track-artist">{track.artists || 'Unknown Artist'}</div>
                    </div>
                    <div className="track-actions">
                      <div className="track-drag" title="Drag to reorder">⠿</div>
                      <button
                        className="track-remove-button"
                        onClick={(e) => handleRemoveTrack(index, e)}
                        title="Remove track"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-playlist">
                <span className="empty-icon">♪</span>
                <p>No tracks in queue</p>
                <button 
                  className="add-queue-button"
                  onClick={() => setShowSearch(true)}
                  style={{ marginTop: '16px', width: 'auto', padding: '8px 16px' }}
                >
                  Add Tracks
                </button>
              </div>
            )}
          </div>
          
          {tracks.length > 0 && (
            <div className="playlist-footer">
              <button 
                className="add-queue-button"
                onClick={() => setShowSearch(true)}
              >
                + Add to Queue
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Mini player */}
      {sidebarCollapsed && (
        <div className="mini-player" key={`mini-${trackUpdateKey}`}>
          <img src={trackInfo.image} className="mini-album-art" alt="" />
          <div className="mini-track-info">
            <div className="mini-track-name">{trackInfo.title}</div>
            <div className="mini-artist-name">{trackInfo.artist}</div>
          </div>
          <div className="mini-controls">
            <button 
              className="mini-play-button"
              onClick={handlePlayPauseSync}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button 
              className="mini-expand-button"
              onClick={() => setSidebarCollapsed(false)}
            >
              ⟩
            </button>
          </div>
        </div>
      )}
      
      {/* Search Modal */}
      {showSearch && (
        <SpotifySearchModal 
          isOpen={showSearch} 
          onClose={() => setShowSearch(false)} 
        />
      )}
    </>
  );
};

export default SpotifyMusicPlayer;